import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SEO from '../../components/common/SEO/SEO';
import { useSEO } from '../../hooks/useSEO';
import { login } from '../../services/authService';
import './Login.css';

const Login = () => {
  const { t } = useTranslation();
  
  // SEO Configuration
  const { seoData } = useSEO({
    title: t('seo.pages.login.title'),
    description: t('seo.pages.login.description'),
    keywords: t('seo.pages.login.keywords'),
    url: '/login',
    noindex: true, // Page de connexion souvent non indexée
    structuredData: {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "Connexion FlowDino",
      "description": t('seo.pages.login.description')
    }
  });

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState(true);
  const [showError, setShowError] = useState(false);
  const navigate = useNavigate();

  // Effet pour gérer l'animation de l'erreur
  useEffect(() => {
    if (error) {
      setShowError(true);
      // Auto-hide error after 5 seconds
      const timer = setTimeout(() => {
        setShowError(false);
        setTimeout(() => setError(''), 300); // Attendre la fin de l'animation
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await login(email, password, rememberMe);
      // Redirection vers la page d'accueil ou tableau de bord selon ce qui est disponible
      navigate('/articles');  // Modification ici - redirection vers une route existante
    } catch (error) {
      console.error('Erreur de connexion détaillée:', error);
      
      // Gestion des différents types d'erreurs avec des messages conviviaux
      let errorMessage = 'Une erreur inattendue s\'est produite. Veuillez réessayer.';
      
      if (error.message.includes('401') || error.message.includes('Authentication failed')) {
        errorMessage = 'Email ou mot de passe incorrect. Veuillez vérifier vos identifiants.';
      } else if (error.message.includes('400')) {
        errorMessage = 'Données de connexion invalides. Veuillez vérifier vos informations.';
      } else if (error.message.includes('500')) {
        errorMessage = 'Erreur du serveur. Veuillez réessayer dans quelques instants.';
      } else if (error.message.includes('Network Error') || error.message.includes('fetch')) {
        errorMessage = 'Problème de connexion réseau. Vérifiez votre connexion internet.';
      } else if (error.message.includes('timeout')) {
        errorMessage = 'La connexion a pris trop de temps. Veuillez réessayer.';
      }
      
      setError(errorMessage);
      setShowError(true);
    } finally {
      setLoading(false);
    }
  };
  return (
    <div className="login-container">
      <SEO {...seoData} />
      
      <div className="login-form-wrapper">
        <h2>Connexion</h2>
        {error && (
          <div className={`alert alert-danger ${showError ? 'show' : ''}`}>
            <strong>Erreur :</strong> {error}
            <button 
              type="button" 
              className="close-error"
              onClick={() => {
                setShowError(false);
                setTimeout(() => setError(''), 300);
              }}
              aria-label="Fermer"
            >
              ×
            </button>
          </div>
        )}
        
        <form onSubmit={handleSubmit} className="login-form">          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                // Réinitialiser l'erreur quand l'utilisateur commence à taper
                if (error) {
                  setShowError(false);
                  setTimeout(() => setError(''), 300);
                }
              }}
              required
              className="form-control"
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="password">Mot de passe</label>
            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                // Réinitialiser l'erreur quand l'utilisateur commence à taper
                if (error) {
                  setShowError(false);
                  setTimeout(() => setError(''), 300);
                }
              }}
              required
              className="form-control"
            />
          </div>
          
          <div className="form-group form-check">
            <input
              type="checkbox"
              id="rememberMe"
              className="form-check-input"
              checked={rememberMe}
              onChange={(e) => setRememberMe(e.target.checked)}
            />
            <label htmlFor="rememberMe" className="form-check-label">
              Se souvenir de moi
            </label>
          </div>
          
          <div className="form-group">
            <button 
              type="submit" 
              className="btn btn-primary btn-block"
              disabled={loading}
            >
              {loading ? 'Connexion en cours...' : 'Se connecter'}
            </button>
          </div>
        </form>
        
        <div className="login-footer">
          <p>
            Vous n'avez pas de compte ? <a href="/register">S'inscrire</a>
          </p>
          <p>
            <a href="/forgot-password">Mot de passe oublié ?</a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
